import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {SORT} from '../../../../incase-domain/common/sort.enum';
import {Role, User} from '../../../../incase-domain/user';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import {IPaginatedResult} from '../../../../incase-domain/search';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';
import DataView, {ViewMode} from '../../../components/common/data/DataView';
import {FilterDefinition, FilterDataType} from '../../../components/common/data/filters/Filter';
import {prettyDateFormat} from '../../../../utils/dateTimeUtils';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {useArchiveUsersMutation, useRestoreUsersMutation} from '../utils/queries';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {canEditUsers, isUserSuperUser} from './utils';
import {DropdownMenuCompactTileProps} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompactTile';
import {createDataViewListStateProps, ListParams, SetListParamsOptions} from '../../../../hooks/listParams';

const USER_FILTER_DEFINITIONS: FilterDefinition[] = [
    {
        type: FilterDataType.BOOLEAN,
        key: 'archived',
        labelTrue: 'usersPage.archived',
        labelFalse: 'usersPage.active',
    },
    {
        type: FilterDataType.STRING_ARRAY,
        key: 'roles',
        options: Object.values(Role).filter((role) => role !== Role.USER),
    },
];

interface UsersPageTableProps {
    isLoading: boolean;
    isFetching: boolean;
    users: IPaginatedResult<User> | undefined;
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
    onImportUser: () => void;
    onCreateUser: () => void;
    onEditUser: (userId: string) => void;
}

export default function UsersPageTable(props: UsersPageTableProps): JSX.Element {
    const {isLoading, isFetching, users, listParams, setListParams, onImportUser, onCreateUser, onEditUser} = props;

    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {t, i18n} = useTranslation();
    const loggedInUser = useSelector(loggedInUserSelector);

    const queryClient = useQueryClient();

    const onArchiveRestorePageChange = useCallback(
        (archivedCount = 1) => {
            if (!users) {
                return;
            }

            const newElCount = users.numberOfElements - archivedCount;
            const newMaxPage = Math.max(Math.ceil(newElCount / users.pageSize), 1);

            if (listParams.page > newMaxPage) {
                setListParams({page: newMaxPage});
            }
        },
        [listParams.page, setListParams, users]
    );

    const {mutate: archiveMutation, isLoading: isArchiving} = useArchiveUsersMutation({
        onSuccess: (_data, userIds) => {
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.user.lists()});
            onArchiveRestorePageChange(userIds.length);

            if (users?.content && selectRow.length > 0) {
                const selectedIds = selectRow.map((row) => row._id);
                const updatedSelectRows = users.content.filter((user) => selectedIds.includes(user._id));
                setSelectRow(updatedSelectRows);
            }
        },
    });

    const {mutate: restoreMutation, isLoading: isRestoring} = useRestoreUsersMutation({
        onSuccess: (_data, userIds) => {
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.user.lists()});
            onArchiveRestorePageChange(userIds.length);

            if (users?.content && selectRow.length > 0) {
                const selectedIds = selectRow.map((row) => row._id);
                const updatedSelectRows = users.content.filter((user) => selectedIds.includes(user._id));
                setSelectRow(updatedSelectRows);
            }
        },
    });

    const [selectedUser, setSelectedUser] = useState({} as User);
    const [archiveUserModal, setArchiveUserModal] = useState(false);
    const [selectRow, setSelectRow] = useState<User[]>([]);
    const isDisabledOrLoading = isArchiving || isRestoring || isLoading || isFetching;
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const renderProfilePicture = (): JSX.Element => {
        return <Button size="lg" square variant="soft" rounded leading="solar:user-outline" />;
    };

    const renderUsername = (user: User): JSX.Element => {
        return (
            <div className="u-flex u-gap-1">
                <strong>{user.username}</strong>
                &bull;
                <div>{user.email}</div>
            </div>
        );
    };

    const renderCreatedAt = (user: User): JSX.Element => {
        return <>{prettyDateFormat(new Date(user.created), i18n.language)}</>;
    };

    function archiveUserFunction(user: User): void {
        if (!user.archived) {
            archiveMutation([user._id]);
        } else {
            restoreMutation([user._id]);
        }
    }

    useEffect(() => {
        if (users?.content && selectedUserIds.length > 0) {
            const updatedSelectRows = users.content.filter((user) => selectedUserIds.includes(user._id));
            setSelectRow(updatedSelectRows);
        }
    }, [users, selectedUserIds]);

    const renderArchive = (user: User): JSX.Element => {
        if (user.archived) {
            return (
                <Button fullWidth className="p-users__table_archivedButton" variant="soft" color="error">
                    {t('usersPage.archived')}
                </Button>
            );
        }
        return (
            <Button fullWidth className="p-users__table_activeButton" variant="soft" color="success">
                {t('usersPage.active')}
            </Button>
        );
    };

    const renderActions = (user: User): JSX.Element => {
        const amISuperuser = loggedInUser?.roles.includes(Role.SUPERUSER);

        if (!amISuperuser) return <></>;

        return (
            <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenuCompact
                    menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                    items={[
                        [
                            {
                                label: t('editorPage.edit'),
                                icon: 'solar:pen-2-linear',
                                onClick: () => onEditUser(user._id),
                            },
                            ...(!user.roles.includes(Role.SUPERUSER || Role.ADMIN) && user.archived
                                ? ([
                                      {
                                          label: t('registerPage.restoreUser'),
                                          icon: 'lucide:archive-restore',
                                          onClick: () => {
                                              setArchiveUserModal(true);
                                              setSelectedUser(user);
                                          },
                                      },
                                  ] as DropdownMenuCompactTileProps[])
                                : ([
                                      {
                                          label: t('registerPage.archiveUser'),
                                          icon: 'solar:archive-linear',
                                          onClick: () => {
                                              setArchiveUserModal(true);
                                              setSelectedUser(user);
                                          },
                                          color: 'error',
                                      },
                                  ] as DropdownMenuCompactTileProps[])),
                        ],
                    ]}
                >
                    {!user.roles.includes(Role.SUPERUSER) && (
                        <Button
                            leading="ph:dots-three-vertical-bold"
                            size="sm"
                            variant="ghost"
                            square
                            aria-label="Show all action dropdown"
                        />
                    )}
                </DropdownMenuCompact>
            </div>
        );
    };

    return (
        <>
            <div className="c-presentationName u-pt-2">
                <DataView
                    views={[
                        {
                            view: ViewMode.TABLE,
                            columns: [
                                {
                                    body: () => renderProfilePicture(),
                                    width: '2rem',
                                },
                                {
                                    label: t('usersPage.user'),
                                    key: 'username',
                                    direction: SORT.NEUTRAL,
                                    sortable: true,
                                    body: (row) => renderUsername(row),
                                },
                                {
                                    key: 'archived',
                                    body: (row) => renderArchive(row),
                                    width: '2rem',
                                    align: 'center',
                                },
                                {
                                    label: t('registerPage.createdAt'),
                                    body: (row) => renderCreatedAt(row),
                                    width: '11rem',
                                },
                                {
                                    body: (row) => renderActions(row),
                                    width: '4rem',
                                },
                            ],
                        },
                    ]}
                    rows={users?.content || []}
                    rowIdentifier={(row) => row._id}
                    onRowClick={(row) => !isUserSuperUser(row) && canEditUsers(loggedInUser) && onEditUser(row._id)}
                    loading={isLoading || isFetching || isArchiving || isRestoring}
                    paginationData={{
                        pageSize: listParams.size,
                        currentPage: listParams.page,
                        itemsCount: users?.numberOfElements || 0,
                    }}
                    onSelected={(selectedRows) => {
                        setSelectedUserIds(selectedRows.map((row) => row._id));
                        setSelectRow(selectedRows);
                    }}
                    selectActions={[
                        ...(selectRow?.some((row) => !row.archived)
                            ? [
                                  {
                                      label: t('registerPage.archiveUsers', {count: selectRow.filter((row) => !row.archived).length}),
                                      onClick: (selected: User[]) => {
                                          archiveMutation(selected.filter((row) => !row.archived).map((row) => row._id));
                                      },
                                      icon: 'solar:archive-linear',
                                      disabled: isDisabledOrLoading,
                                      loading: isDisabledOrLoading,
                                  },
                              ]
                            : []),
                        ...(selectRow?.some((row) => row.archived)
                            ? [
                                  {
                                      label: t('registerPage.restoreUsers', {count: selectRow.filter((row) => row.archived).length}),
                                      onClick: (selected: User[]) => {
                                          restoreMutation(selected.filter((row) => row.archived).map((row) => row._id));
                                      },
                                      icon: 'lucide:archive-restore',
                                      disabled: isDisabledOrLoading,
                                      loading: isDisabledOrLoading,
                                  },
                              ]
                            : []),
                    ]}
                    headerActionPrimary={{icon: 'ph:plus-bold', onClick: onCreateUser, title: t('genericTable.new')}}
                    headerActionSecondary={{
                        icon: 'solar:upload-minimalistic-linear',
                        onClick: onImportUser,
                        title: t('registerPage.import'),
                    }}
                    {...dataViewListState}
                    enableSearch
                    selectedItems={selectRow}
                    filterDefinitions={USER_FILTER_DEFINITIONS}
                />
            </div>

            <ConfirmModal
                isOpen={archiveUserModal}
                message={
                    selectedUser.archived
                        ? `${t('registerPage.restoreConfirmation')} ${selectedUser.username}?`
                        : `${t('registerPage.archiveConfirmation')} ${selectedUser.username}?`
                }
                onCancel={() => setArchiveUserModal(false)}
                onConfirm={() => {
                    archiveUserFunction(selectedUser);
                    setArchiveUserModal(false);
                }}
                showCancel
                showConfirm
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                icon={selectedUser.archived ? 'solar:archive-up-minimlistic-bold' : 'solar:archive-bold'}
                color={selectedUser.archived ? 'success' : 'error'}
            />
        </>
    );
}
